import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { tokenStore } from '@/utils/tokenStore';
import { logoutRequest } from '@/api/auth';
import type { AuthSession } from '@/api/auth';
import type { UserProfile } from '@/types';
import { AuthContext } from './authContextDef';
import { env } from '@/config/env';
import { api } from '@/api/client';
import { identifyUser, resetUser } from '@/utils/analytics';

const SESSION_USER_KEY = 'andhub_user';
const LOGGED_IN_KEY = 'andhub_logged_in';

function isLoggedInFlagSet(): boolean {
  return localStorage.getItem(LOGGED_IN_KEY) === 'true';
}

function setLoggedInFlag() {
  localStorage.setItem(LOGGED_IN_KEY, 'true');
}

function clearLoggedInFlag() {
  localStorage.removeItem(LOGGED_IN_KEY);
}

function readCachedUser(): UserProfile | null {
  try {
    const stored = sessionStorage.getItem(SESSION_USER_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as UserProfile;
    // Discard stale cache entries that predate the RBAC migration
    if (!Array.isArray(parsed.roles) || !Array.isArray(parsed.permissions)) {
      sessionStorage.removeItem(SESSION_USER_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  photoUrl: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const cachedUser = readCachedUser();

  // Public pages where a session refresh makes no sense.
  const isPublicPage = ['/login', '/callback'].includes(window.location.pathname);
  // If the logged-in flag is absent, we definitely have no session — skip refresh.
  const mightBeLoggedIn = isLoggedInFlagSet();

  const [state, setState] = useState<AuthState>({
    user: cachedUser,
    isAuthenticated: !!cachedUser && mightBeLoggedIn,
    // Only show loading (and attempt refresh) if we have evidence of a prior session.
    isLoading: !isPublicPage && mightBeLoggedIn,
    photoUrl: null,
  });

  const photoUrlRef = useRef<string | null>(null);

  function fetchPhoto(hasPhoto?: boolean) {
    if (hasPhoto === false) return;
    api.getBlob('/api/me/photo').then(blob => {
      if (!blob) return;
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      const url = URL.createObjectURL(blob);
      photoUrlRef.current = url;
      setState(prev => ({ ...prev, photoUrl: url }));
    }).catch(() => {});
  }

  useEffect(() => {
    if (isPublicPage || !mightBeLoggedIn) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const controller = new AbortController();
    let refreshed = false; // did THIS tab actually call /auth/refresh?

    // runWithRefreshLock ensures only one tab at a time calls /auth/refresh.
    // Web Locks API (all modern browsers): tabs queue and each calls /auth/refresh
    // sequentially with the freshly-rotated cookie — no simultaneous refresh.
    // localStorage fallback: Tab B waits for Tab A's broadcast, then resolves
    // without calling fn() — the .then() handler below catches that case.
    tokenStore.runWithRefreshLock(async () => {
      if (controller.signal.aborted) return;
      // Another holder of the lock (another tab, or api/client's ensureToken gate
      // in this tab) already refreshed — reuse that token instead of rotating the
      // refresh cookie a second time. Handled by the `refreshed === false` branch
      // in .then() below.
      if (tokenStore.getAccessToken()) return;

      const res = await fetch(`${env.apiUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });

      if (res.status === 401) {
        const e = new Error('token_expired') as Error & { isTokenExpired: true };
        e.isTokenExpired = true;
        throw e;
      }
      if (!res.ok) throw new Error('transient_error');

      const data = await res.json() as AuthSession;
      tokenStore.setAccessToken(data.accessToken); // also broadcasts to other tabs
      sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(data.user));
      refreshed = true;

      if (!controller.signal.aborted) {
        setState({ user: data.user, isAuthenticated: true, isLoading: false, photoUrl: null });
        Sentry.setUser({ id: String(data.user.id), email: data.user.email });
        identifyUser(data.user);
        fetchPhoto(data.user.hasPhoto);
      }
    })
      .then(() => {
        if (controller.signal.aborted || refreshed) return;
        // Reached only in the localStorage fallback path: fn() was not called
        // (Tab B waited for Tab A's broadcast). Token is set in memory via broadcast.
        if (cachedUser) {
          // Render from the cached profile first so the tab does not flash,
          // then confirm it against the server. This branch never calls
          // /auth/refresh, so without the re-check the tab keeps whatever
          // roles and permissions sessionStorage booted with — for a tab left
          // open across a role change (or a database restore) that is stale
          // admin UI that never corrects itself.
          setState({ user: cachedUser, isAuthenticated: true, isLoading: false });
          Sentry.setUser({ id: String(cachedUser.id), email: cachedUser.email });
          identifyUser(cachedUser);
          fetchPhoto(cachedUser.hasPhoto);
          api.get<UserProfile>('/api/me')
            .then(me => {
              if (controller.signal.aborted) return;
              sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(me));
              // Same person, so the Sentry/analytics identity set above still
              // holds — only the roles and permissions can have moved.
              setState(prev => ({ ...prev, user: me, isAuthenticated: true, isLoading: false }));
            })
            // Transient: keep the cached profile rather than bouncing a
            // working tab to the login page. The API is the real gate.
            .catch(() => {});
        } else if (tokenStore.getAccessToken()) {
          // Have token but no sessionStorage user (fresh tab) — fetch profile
          api.get<UserProfile>('/api/me')
            .then(me => {
              if (controller.signal.aborted) return;
              sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(me));
              setState({ user: me, isAuthenticated: true, isLoading: false, photoUrl: null });
              Sentry.setUser({ id: String(me.id), email: me.email });
              identifyUser(me);
              fetchPhoto(me.hasPhoto);
            })
            .catch(() => {
              if (controller.signal.aborted) return;
              tokenStore.invalidateAccessToken();
              clearLoggedInFlag();
              window.location.href = '/login';
            });
        } else {
          sessionStorage.removeItem(SESSION_USER_KEY);
          clearLoggedInFlag();
          window.location.href = '/login';
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) return;
        if (err instanceof Error && (err as { isTokenExpired?: boolean }).isTokenExpired) {
          tokenStore.broadcastRefreshFailed();
          sessionStorage.removeItem(SESSION_USER_KEY);
          clearLoggedInFlag();
          window.location.href = cachedUser ? '/login?error=session_expired' : '/login';
          return;
        }
        // Transient failure — keep session alive.
        tokenStore.broadcastRefreshTransientFail();
        setState((prev) => ({ ...prev, isLoading: false }));
      });

    return () => { controller.abort(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback((data: AuthSession) => {
    tokenStore.setAccessToken(data.accessToken);
    sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(data.user));
    setLoggedInFlag();
    setState({ user: data.user, isAuthenticated: true, isLoading: false, photoUrl: null });
    Sentry.setUser({ id: String(data.user.id), email: data.user.email });
    identifyUser(data.user);
    fetchPhoto(data.user.hasPhoto);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patchUser = useCallback((patch: Partial<UserProfile>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const updated = { ...prev.user, ...patch };
      sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(updated));
      return { ...prev, user: updated };
    });
  }, []);

  const logout = useCallback(() => {
    const token = tokenStore.getAccessToken();
    tokenStore.clearAccessToken();
    sessionStorage.removeItem(SESSION_USER_KEY);
    clearLoggedInFlag();
    if (photoUrlRef.current) { URL.revokeObjectURL(photoUrlRef.current); photoUrlRef.current = null; }
    setState({ user: null, isAuthenticated: false, isLoading: false, photoUrl: null });
    Sentry.setUser(null);
    resetUser();
    if (token) {
      logoutRequest(token).catch(() => {});
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, patchUser }}>
      {children}
    </AuthContext.Provider>
  );
}
